import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { rgba } from 'polished';
import { defineMessages, useIntl } from 'react-intl';

import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import RemoveDefaultButtonStyles from '../styles/RemoveDefaultButtonStyles';
import getIconSizeClassName from '../functions/getIconSizeClassName';
import AccordionBase from '../components/AccordionBase';
import IconFilters from '../components/icons/IconFilters';
import CondensedTheme from '../components/CondensedTheme';


function AccordionFilters({
    primaryFilters,
    additionalFilters,
    areAdditionalFiltersHidden,
    columnCount,
    className,
    ...otherProps
}) {
    const [ isExpanded, setIsExpanded ] = useState(false);

    function handleAccordionClick() {
        setIsExpanded(!isExpanded);
    }

    const hasAdditionalFilters = additionalFilters.length > 0;

    return (
        <CondensedTheme>
            <Container
                className={classNames('rh-p-1', className)}
                columnCount={columnCount ?? primaryFilters.length}
                {...otherProps}
            >
                <AccordionBase
                    name="AccordionFilters"
                    isExpanded={isExpanded && hasAdditionalFilters && !areAdditionalFiltersHidden}
                    onClick={handleAccordionClick}
                    HeaderComponent={FilterHeader}
                    headerProps={{
                        filters: primaryFilters,
                        areAdditionalFiltersHidden: !hasAdditionalFilters || areAdditionalFiltersHidden,
                    }}
                    alwaysRenderChildMarkup
                >
                    <>
                        <If condition={hasAdditionalFilters}>
                            <div
                                className="grid-container rh-display-grid rh-mt-1"
                                data-test-name="additional-filters"
                            >
                                {additionalFilters}
                            </div>
                        </If>
                    </>
                </AccordionBase>
            </Container>
        </CondensedTheme>
    );
}

AccordionFilters.propTypes = {
    primaryFilters: PropTypes.arrayOf(
        PropTypes.node,
    ).isRequired,
    additionalFilters: PropTypes.arrayOf(
        PropTypes.node,
    ),
    // This prop is used for instances where additional filters are provided
    //  to remain in the form, but all are hidden to the user. This prop
    //  can be used to force the additional filters to be hidden.
    areAdditionalFiltersHidden: PropTypes.bool,
    columnCount: PropTypes.number,
    className: PropTypes.string,
};

AccordionFilters.defaultProps = {
    additionalFilters: [],
    areAdditionalFiltersHidden: false,
    columnCount: undefined,
    className: undefined,
};

/* Disabled lint errors due to creating FilterHeader in this file */
/* eslint-disable react/prop-types */
/* eslint-disable react/no-multi-comp */

function FilterHeader({ isExpanded, filters, areAdditionalFiltersHidden, onClick, onKeyPress }) {
    const intl = useIntl();

    return (
        <div className="flex-container rh-display-flex rh-flex-nowrap">
            <div
                className="flex-left-space grid-container rh-display-grid"
                data-test-name="primary-filters"
            >
                {filters}
            </div>
            <If condition={!areAdditionalFiltersHidden}>
                <button
                    type="button"
                    className={classNames('filter-button rh-ml-0_5 rh-position-relative', {
                        'is-expanded': isExpanded,
                    })}
                    /* Accordions should be toggled with either Enter or Space -
                        Space is handled in the onClick for a button by default */
                    onClick={onClick}
                    onKeyPress={e => e?.key === 'Enter' && onKeyPress(e)}
                    data-name="AccordionFilters-toggleFilters"
                    aria-label={intl.formatMessage(MESSAGES.TOGGLE_BUTTON)}
                >
                    <IconFilters
                        className={`icon-filter ${getIconSizeClassName('L')}`}
                        outlineWidth="0"
                    />
                </button>
            </If>
        </div>
    );
}

const GRID_GAP = Sizes.SPACING.HALF;
const FILTER_BUTTON_TRIANGLE_WIDTH = '5px';
const FILTER_BUTTON_WIDTH = Sizes.SPACING.THREE;


const Container = styled.div`
    background-color: ${rgba(Colours.STONE_LIGHT, 0.7)};

    .flex-container {
        > .flex-left-space {
            flex-grow: 1;
        }

        > .filter-button {
            ${RemoveDefaultButtonStyles};

            flex: 0 0 ${FILTER_BUTTON_WIDTH};
            align-self: center;

            /* Need exact width so we can add CSS triangle */
            width: ${Sizes.SPACING.THREE};

            /* Negative margin to account for icon space */
            margin-right: -${Sizes.SPACING.HALF};

            cursor: pointer;

            > .icon-filter {
                stroke: ${Colours.BLUEBERRY_DARK};
                transform: rotate(0);
                transition: stroke 300ms;
            }

            &::after {
                position: absolute;
                bottom: 100%;
                left: calc(50% - ${FILTER_BUTTON_TRIANGLE_WIDTH});

                content: '';
                width: 0;
                height: 0;

                border: ${FILTER_BUTTON_TRIANGLE_WIDTH} solid transparent;
                border-top-width: 0;

                transition: border-bottom-color 300ms;
            }

            &.is-expanded::after {
                border-bottom-color: ${Colours.BLUEBERRY_DARK};
            }

            &:hover,
            &:focus {
                > .icon-filter {
                    stroke: ${Colours.BLUEBERRY};
                }

                &.is-expanded::after {
                    border-bottom-color: ${Colours.BLUEBERRY};
                }
            }
        }
    }

    .grid-container {
        /* We want to auto-fit our children into our space using minmax to automatically determine the rows/columns needed.
            The minimum value is the maximum of 12rem which is the breakpoint to decrease columns
                OR 100% minus our column gap divided by 3 to ensure we have a MAX of 3 columns at all times
            The maximum value in the minmax is 1fr to ensure even widths. */
        grid-template-columns: ${props =>
        `repeat(auto-fill, minmax(max(12rem, calc((100% - (${GRID_GAP} * ${props.columnCount})) / ${props.columnCount})), 1fr))`
};
        grid-column-gap: ${GRID_GAP};
        grid-row-gap: ${GRID_GAP};

        /* reduce the grid width by the width of the filter button above in the flex container */
        width: calc(100% - ${FILTER_BUTTON_WIDTH});
    }
`;

const MESSAGES = defineMessages({
    TOGGLE_BUTTON: {
        id: 'base-ui.AccordionFilter.toggleButtonLabel',
        defaultMessage: 'Toggle filters',
    },
});

export default observer(AccordionFilters);
